import { useEffect, useRef } from 'react';
import { Button, Flex, Stack, Text, Textarea } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconSparkles } from '@tabler/icons-react';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useTemplateInstruction } from '../hooks/useTemplateInstruction';
import { useSaveTemplateInstruction } from '../hooks/useSaveTemplateInstruction';

const ADDITIONAL_INSTRUCTIONS_MAX_LENGTH = 2000;

type Props = {
  templateId: string | undefined;
  displayName: string | undefined;
  /** Whether this is the template we write automatic notes with. */
  isAutomatic: boolean;
  eligibleForSummarisation: boolean;
  value: string;
  onChange: (value: string) => void;
};

export function AdditionalInstructionsField({
  templateId,
  displayName,
  isAutomatic,
  eligibleForSummarisation,
  value,
  onChange,
}: Props) {
  const { flagEnabledDefaultTemplates } = useFlags();
  const { savedInstruction, isLoading, isError, error } = useTemplateInstruction(templateId);
  const { saveInstruction, isSaving } = useSaveTemplateInstruction();

  // Pre-populate with the template's saved instruction once its GET resolves, but never
  // over text the user has already typed (e.g. while the GET was still in flight). We
  // snapshot the box value when the template changes and only apply the saved default if
  // the box is still untouched since that selection. Switching templates intentionally
  // re-applies the new template's saved default.
  const appliedForTemplateId = useRef<string | undefined>(undefined);
  const valueAtSelection = useRef<string>(value);
  useEffect(() => {
    appliedForTemplateId.current = undefined;
    valueAtSelection.current = value;
  }, [templateId]);
  useEffect(() => {
    if (!templateId || isLoading || appliedForTemplateId.current === templateId) {
      return;
    }
    appliedForTemplateId.current = templateId;
    if (value === valueAtSelection.current) {
      onChange(savedInstruction);
    }
  }, [templateId, isLoading, savedInstruction, value, onChange]);

  const reportedErrorRef = useRef(false);
  useEffect(() => {
    if (isError && !reportedErrorRef.current) {
      reportedErrorRef.current = true;
      notifyAndLogError(
        'Failed to load saved instructions',
        "We couldn't load your saved instructions for this template.",
        error,
        { templateId },
      );
    }
    if (!isError) {
      reportedErrorRef.current = false;
    }
  }, [isError, error, templateId]);

  const disabled = !eligibleForSummarisation;
  // A template that skips additional instructions skips remembered ones in automatic notes too.
  const showsAutomaticHint = flagEnabledDefaultTemplates && isAutomatic && !disabled;

  const savedNotification = () => {
    if (!flagEnabledDefaultTemplates) {
      return {
        title: 'Instructions saved',
        message: 'These will pre-populate next time you use this template.',
      };
    }
    const templateName = displayName ?? 'this template';
    return {
      title: 'Instructions remembered',
      message: isAutomatic
        ? `We'll use these whenever you write notes with ${templateName}, including your automatic notes.`
        : `We'll fill these in for you next time you use ${templateName}.`,
    };
  };

  const handleSave = async () => {
    if (!templateId) {
      return;
    }
    try {
      await saveInstruction(templateId, value.trim());
      notifications.show({ ...savedNotification(), color: 'green' });
    } catch (saveError) {
      notifyAndLogError('Failed to save instructions', 'Please try again later', saveError, {
        templateId,
      });
    }
  };

  return (
    <Stack gap="xs">
      <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
        Give Siesta additional instructions
      </Text>
      <Textarea
        aria-label="Additional instructions for Siesta"
        placeholder="e.g. keep the tone warm and friendly, be more concise, or focus on the action points"
        value={value}
        onChange={event => onChange(event.currentTarget.value)}
        maxLength={ADDITIONAL_INSTRUCTIONS_MAX_LENGTH}
        autosize
        minRows={3}
        maxRows={6}
        disabled={disabled}
        description={disabled ? 'This template skips additional instructions.' : undefined}
      />
      <Flex justify={showsAutomaticHint ? 'space-between' : 'flex-end'} align="center" gap="sm">
        {showsAutomaticHint && (
          <Flex align="center" gap="xxxs">
            <IconSparkles size={14} color="var(--mantine-color-brand-6)" aria-hidden />
            <Text size="xs" c="gray.7">
              Remembered instructions also shape your automatic notes
            </Text>
          </Flex>
        )}
        <Button
          variant="light"
          size="xs"
          onClick={handleSave}
          loading={isSaving}
          disabled={disabled || !templateId}
        >
          Remember for this template
        </Button>
      </Flex>
    </Stack>
  );
}
